import { getFragment, type FragmentType } from "@/api"
import type { CachedRelayConnection, CachedRelayEdge } from "@/api/client"
import SentryReact from "@/common/sentry_client"
import { isApolloError, type ApolloError, type TypedDocumentNode } from "@apollo/client"
import type { ApolloCache, Modifier, ModifierDetails, Reference } from "@apollo/client/cache"
import type { DocumentTypeDecoration } from "@graphql-typed-document-node/core"
import type { AddToast } from "@skyltmax/ds9/hooks"

/** Shows an error toast with the first resolver error or a fallback message. */
export const apolloErrorToast = (toast: AddToast, fallback: string) => {
  return (error: ApolloError) => {
    if (error.graphQLErrors[0] && error.graphQLErrors.length > 0) {
      return toast(error.graphQLErrors[0].message, "error")
    }

    console.error(error)
    toast(fallback, "error")
    return
  }
}

export const apolloMutationCatch = (toast: AddToast, fallback: string) => {
  return (error: ApolloError) => {
    if (isApolloError(error)) {
      if (error.networkError?.name === "AbortError") {
        return
      }

      apolloErrorToast(toast, fallback)(error)
    } else {
      console.error(error)
      SentryReact.captureException(error)
    }
  }
}

/**
 * Apollo cache.modify helpers for proper typing.
 */

/** Append single item to existing list. */
export const cacheItemAppend = <TType>(
  cache: ApolloCache<any>,
  idField: string,
  item: FragmentType<DocumentTypeDecoration<TType, any>>,
  fragment: TypedDocumentNode<TType, any>
): Modifier<any> => {
  const modifier = <T extends readonly Reference[] | readonly Record<string, any>[]>(
    existing: T,
    { readField, toReference }: ModifierDetails
  ): readonly Reference[] | readonly Record<string, any>[] => {
    const existingRefs = existing.map(ref => toReference(ref)).filter((ref): ref is Reference => !!ref)

    const newItemRef = cache.writeFragment({
      data: getFragment(fragment, item),
      fragment: fragment,
    })

    if (!newItemRef || existingRefs.some(ref => readField(idField, ref) === item[idField])) {
      return existingRefs
    }

    return [...existingRefs, newItemRef]
  }

  return modifier
}

/** Append list of items to existing list. */
export const cacheListAppend = <TType>(
  cache: ApolloCache<any>,
  idField: string,
  items: ReadonlyArray<FragmentType<DocumentTypeDecoration<TType, any>>>,
  fragment: TypedDocumentNode<TType, any>,
  fragmentName?: string
): Modifier<any> => {
  const modifier = <T extends readonly Reference[] | readonly Record<string, any>[]>(
    existing: T,
    { readField, toReference }: ModifierDetails
  ): readonly Reference[] | readonly Record<string, any>[] => {
    const existingRefs = existing.map(ref => toReference(ref)).filter((ref): ref is Reference => !!ref)
    const existingIds = existingRefs.map(ref => readField(idField, ref))

    const newRefs = items
      .map(item => {
        return cache.writeFragment({
          data: getFragment(fragment, item),
          fragment,
          fragmentName,
        })
      })
      .filter((ref): ref is Reference => !existingIds.includes(readField(idField, ref)) && !!ref)

    return [...existingRefs, ...newRefs]
  }

  return modifier
}

/** Remove items with IDs from list. */
export const cacheListRemove = (idField: string, ids: string[]): Modifier<any> => {
  const modifier = <T extends readonly Reference[] | readonly Record<string, any>[]>(
    existing: T,
    { readField, toReference }: ModifierDetails
  ): readonly Reference[] | readonly Record<string, any>[] => {
    return [...existing]
      .map(e => toReference(e))
      .filter((ref): ref is Reference => !!ref && !ids.includes(readField(idField, ref) || ""))
  }

  return modifier
}

/** Remove edges with IDs from list. */
export const cacheEdgesRemove = (idField: string, ids: string[]): Modifier<any> => {
  const modifier = <T extends Reference | CachedRelayConnection>(
    existing: T,
    { readField, isReference }: ModifierDetails
  ): Reference | CachedRelayConnection => {
    const edges = isReference(existing)
      ? readField<CachedRelayEdge[]>("edges", existing)
      : existing.edges?.map(edge => edge)

    const newEdges = edges?.filter(ref => !ids.includes(readField(idField, ref.node) || "")) || []

    return { ...existing, edges: newEdges }
  }

  return modifier
}
